import short from 'short-uuid';
import { StateCreator } from 'zustand';

import { CampaignsByUserIdQuery } from '../API';
import { api } from '../services';
import { Campaign, ICampaign } from '../typings';

type CampaignsStore = {
  campaigns: CampaignsByUserIdQuery;
  fetchCampaigns: (userId: string) => Promise<void>;
  resetCampaigns: () => void;
  addCampaign: (campaign: ICampaign, userId: string) => Promise<NonNullable<Campaign>>;
  updateCampaign: (id: string, campaign: ICampaign) => Promise<NonNullable<Campaign>>;
};

const initialState: CampaignsByUserIdQuery = { campaignsByUserId: null };

export const createCampaignState: StateCreator<CampaignsStore> = (set, get) => ({
  campaigns: initialState,
  resetCampaigns: () => {
    set({ campaigns: initialState });
  },
  fetchCampaigns: async (userId: string) => {
    const campaignsState = get().campaigns;
    const campaignsResponse = await api.listCampaigns({
      nextToken: campaignsState.campaignsByUserId?.nextToken,
      userId,
    });

    if (campaignsResponse && campaignsResponse.items)
      set({
        campaigns: {
          campaignsByUserId: {
            __typename: campaignsResponse.__typename,
            items: [...(campaignsState.campaignsByUserId?.items || []), ...campaignsResponse.items],
            nextToken: campaignsResponse.nextToken,
          },
        },
      });
  },
  addCampaign: async (campaign, userId) => {
    const campaignsState = get().campaigns;
    const id = short.generate();
    const createdCampaign = await api.createCampaign({
      input: {
        id,
        userId,
        name: campaign.name,
        templateId: campaign.templateId,
        width: campaign.width,
        customText: campaign.customText,
      },
    });

    if (!createdCampaign) throw new Error('Campaign creation failed!');

    if (createdCampaign && campaignsState && campaignsState.campaignsByUserId) {
      set({
        campaigns: {
          campaignsByUserId: {
            ...campaignsState.campaignsByUserId,
            items: [createdCampaign, ...(campaignsState.campaignsByUserId.items || [])],
          },
        },
      });
    }

    return createdCampaign;
  },
  updateCampaign: async (id, campaign) => {
    const campaignsState = get().campaigns;
    const updatedCampaign = await api.updateCampaign({
      input: {
        id,
        name: campaign.name,
        templateId: campaign.templateId,
        width: campaign.width,
        customText: campaign.customText,
      },
    });

    if (!updatedCampaign) throw new Error('Campaign update failed!');

    if (updatedCampaign && campaignsState && campaignsState.campaignsByUserId) {
      const items = [...(campaignsState.campaignsByUserId.items || [])];
      const updatedCampaignIndex = items.findIndex((campaign) => campaign?.id === id);

      items[updatedCampaignIndex] = updatedCampaign;

      set({
        campaigns: {
          campaignsByUserId: {
            ...campaignsState.campaignsByUserId,
            items,
          },
        },
      });
    }

    return updatedCampaign;
  },
});
